import clsx from "clsx";
import { useEnvContext } from "context/EnvContext";
import {
  APP_CLINIC,
  PREDICAMENT_STATE_UNKNOWN,
  PREDICAMENT_STATE_YES,
  SEALD_REDACTED,
  SEARCH_ACTION_EDIT_SEARCH,
  TRACK_EVENTS,
} from "core/consts";
import { descriptiveWhitelist, findSearchAction } from "core/model/auctions";
import { getPatientName } from "core/model/patients";
import { getDecryptedValue } from "core/model/patients/encryption";
import { convertBirthDateIn } from "core/model/utils/dates";
import { formatLocation } from "core/model/utils/location/formatters";
import { OntologyType } from "core/model/utils/ontologies";
import { renderMultiLine } from "core/model/utils/text";
import {
  Auction,
  EncryptedField as EncryptedValue,
  GetOntologyType,
  Location,
  Patient,
  PatientWhitelistDefinition,
} from "core/types";
import { Chip, PrintableChip } from "ds_legacy/components/Chip";
import { LockSecurePin } from "ds_legacy/components/Icons/SecurePin";
import InfoCard, {
  CardContentContainer,
  InfoCardLite,
} from "ds_legacy/components/InfoCard";
import RSButton from "ds_legacy/components/RSButton";
import Tooltip from "ds_legacy/components/Tooltip";
import {
  ICON_DARK,
  STATUS_BADGE_MEDIUM_GREY,
} from "ds_legacy/materials/colors";
import {
  dp,
  margin,
  padding,
  sizing,
  space,
  translate,
} from "ds_legacy/materials/metrics";
import {
  Body,
  Chip as ChipText,
  FONT_SIZE_12,
  FONT_WEIGHT_BOLD,
  FONT_WEIGHT_REGULAR,
  Subheading,
  Title,
} from "ds_legacy/materials/typography";
import { useEncryption, usePrint } from "dsl/atoms/Contexts";
import { useTailwindMedia } from "dsl/atoms/ResponsiveMedia";
import { useCareseekerNavigationHandlers } from "dsl/hooks";
import { ChevronLeftIcon, LockIcon } from "lucide-react";
import React, {
  ComponentType,
  CSSProperties,
  MouseEvent,
  ReactNode,
  useCallback,
  useMemo,
} from "react";
import { useTracking } from "react-tracking";
import styled from "styled-components";
import { useLocale, useTranslations } from "translations";
import Translations from "translations/types";
import { usePatientInfoContext, usePatientInfoSlug } from "./index";
import {
  computeChipProps,
  generatePrefixedString,
  getColonedPrefix,
  getKey,
  isNotEmptyString,
  isNullValue,
} from "./utils";

export type EncryptedFieldProps = {
  bold?: boolean;
  hideEmpty?: boolean;
  isSealdOnly: boolean;
  longContext?: boolean;
  prefix: string;
  suffix?: string | null | undefined;
  testId: string;
  value: EncryptedValue | null | undefined;
  verticalLayout?: boolean;
  withDateString?: boolean;
  withEncryptionAccess: boolean;
};

export type EmptiableFieldProps = {
  bold?: boolean;
  category?: string;
  noEmptyValue?: boolean;
  prefix?: string | null | undefined;
  testId: string;
  title?: string;
  value: string | null | undefined;
};

export type ValueGetter = (auction: Auction | undefined) => any;

export type CategoryType = {
  Component: ComponentType<any>;
  exists: boolean;
  fullWidth?: boolean;
  key: string;
  notEmpty?: boolean;
  valueGetter: (value: Auction) => any;
};

export type ContentWrapperType = (
  content: React.ReactNode | null | undefined,
) => React.JSX.Element;

export type FieldValue = Array<number> | Value;
type Value = boolean | number | string;

const lockIconStyle: CSSProperties = {
  color: ICON_DARK,
  display: "inline",
  fontSize: FONT_SIZE_12,
  height: dp(12),
  minWidth: dp(12),
  verticalAlign: "baseline",
  width: dp(12),
};

export const blurStyle: CSSProperties = {
  background: "white",
  filter: "blur(0.3rem)",
  userSelect: "none",
} as const;

export const italic: CSSProperties = {
  color: STATUS_BADGE_MEDIUM_GREY,
  fontStyle: "Italic",
};

export const BodyWrapper = styled(Body)<{
  column?: boolean;
  margin?: string;
  whiteSpace?: string;
}>`
  display: flex;
  flex-direction: ${({ column }) => (column ? "column" : "row")};
  margin: ${({ margin: m }) => m ?? margin(0, 2)};
  white-space: ${({ whiteSpace }) => whiteSpace ?? "normal"};
`;

export const CategoryContainer = ({
  children,
  className,
  testId,
}: {
  children: ReactNode;
  className?: string;
  testId: string;
}) => {
  return (
    <div
      className={clsx(
        "flex w-full flex-col space-y-0.5 print:max-w-full print:break-inside-avoid print:flex-row print:flex-wrap print:items-baseline",
        className,
      )}
      data-testid={`${testId}_category_container`}
    >
      {children}
    </div>
  );
};

export const BoxLayout = styled.div`
  box-sizing: border-box;
  @media screen {
    display: flex;
    flex: 1 0 auto;
    width: 100%;
    max-width: ${dp(750)};
    padding-top: ${space(2)};
  }

  @media screen {
    & {
      padding-left: ${space(3)};
    }
  }

  @media screen and (max-width: 1360px) {
    & {
      padding-left: ${space(3)};
    }
  }

  @media screen and (max-width: 760px) {
    & {
      padding-left: 0px;
      width: 100vw;
    }
  }
`;

export const InfosBox = styled(BoxLayout)`
  @media screen {
    padding-bottom: ${space(15)};
    padding-right: ${space(0)};
    align-items: stretch;
    flex-direction: column;
  }
`;

export const isWhitelisted = (
  whitelist: PatientWhitelistDefinition,
  auction: Auction | null | undefined,
): boolean => {
  return (
    (auction &&
      descriptiveWhitelist(whitelist)({
        careseeker: auction.patient.careseeker,
        formInputValue: auction,
      })) ||
    false
  );
};

export const isInfectionsAndGermsPredicamentNegative = (
  value: { infection_and_germs_state?: number } | null | undefined,
  translations: Translations,
): [boolean, string] => [
  !!value && value.infection_and_germs_state !== PREDICAMENT_STATE_YES,
  !!value && value.infection_and_germs_state === PREDICAMENT_STATE_UNKNOWN
    ? translations.general.unknown
    : translations.auctionRequest.noGermsInfections,
];

export const DisplayUserIdOrPatientName = ({
  bold,
  hyphen,
  parenthesis,
  patient,
  prefix,
  showName,
  translations,
}: {
  bold?: boolean;
  hyphen?: boolean;
  parenthesis?: boolean;
  patient: Patient | null | undefined;
  prefix: string | null | undefined;
  showName: boolean;
  translations: Translations;
}) => {
  const patientName = (showName && patient && getPatientName(patient)) || null;
  const userId = patient?.user_id;
  const fontWeight = bold ? FONT_WEIGHT_BOLD : FONT_WEIGHT_REGULAR;
  const prefixStyle = { style: { fontWeight: FONT_WEIGHT_REGULAR } };
  const userIdSlug = (content: string) =>
    userId ? (parenthesis ? ` (${content})` : " " + content) : null;

  const join = hyphen ? " - " : " ";

  return patientName ? (
    <span
      style={{
        fontWeight,
        textTransform: "none",
      }}
    >
      {prefix && <span {...prefixStyle}>{`${prefix}${join}`}</span>}
      {patientName}
      {userId && <span>{` (${userId})`}</span>}
    </span>
  ) : (
    <>
      {prefix && <span {...prefixStyle}>{prefix}</span>}
      <span style={{ fontWeight }}>
        {" "}
        {userIdSlug(`${translations.patient.patient} ${userId}`)}
      </span>
    </>
  );
};

export const ActionBanner = ({
  action,
  actions,
  noBorder,
  patient,
  text,
}: {
  action: (() => void) | undefined;
  actions?: Array<React.ReactNode>;
  noBorder?: boolean;
  patient?: Patient | null | undefined;
  text: string;
}) => {
  const translations = useTranslations();
  const { app } = useEnvContext();

  const onClick = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      if (action) action();
    },
    [action],
  );

  return (
    <InfoCard cardMargin="0px 0px -1px 0px" noBorder={noBorder}>
      <CardContentContainer noBorder={noBorder} noPadding>
        <div className="flex h-[42px] w-full justify-between p-1">
          <RSButton
            color="grey"
            id="back_to_search"
            LeftIcon={ChevronLeftIcon}
            loading="na"
            style={{ margin: margin(0) }}
            variant="text"
            onClick={onClick}
          >
            <div className="w-full">
              <DisplayUserIdOrPatientName
                bold
                hyphen
                parenthesis
                patient={patient}
                prefix={text}
                showName={app === APP_CLINIC}
                translations={translations}
              />
            </div>
          </RSButton>
          <div className="flex items-center">{actions}</div>
        </div>
      </CardContentContainer>
    </InfoCard>
  );
};

function Prefix({
  bold,
  endContent,
  text,
  translations,
}: {
  bold: boolean;
  endContent?: React.ReactNode;
  text: string | null | undefined;
  translations: Translations;
}) {
  return (
    <span
      className={clsx("text-body-md", {
        "font-bold": bold,
        "font-normal": !bold,
      })}
      style={{ marginRight: "-12px" }}
    >
      {`${text}${translations.general.colon} `}
      {endContent}
    </span>
  );
}

function PrettyStringAndLockIcon({ value }: { value: string }) {
  const trimmed = value.trim();
  const lastSpace = value.lastIndexOf(" ") + 1;

  let start = trimmed,
    end = "";
  if (lastSpace > 0) {
    start = trimmed.substring(0, lastSpace);
    end = trimmed.substring(lastSpace);
  }

  return (
    <>
      {start}
      <span className="whitespace-nowrap">
        {end}
        <LockIcon
          size={lockIconStyle.fontSize}
          style={{
            ...lockIconStyle,
            margin: margin(0, 0, 0, 2),
          }}
        />
      </span>
    </>
  );
}

function ChipValue({
  chipProps,
  keyPrefix,
  prefix,
  style,
  suffix,
  textBreak,
  value,
}: {
  chipProps: {
    bold?: boolean;
    disabled?: boolean;
    primary?: boolean;
    selected?: boolean;
  };
  keyPrefix?: string;
  prefix?: string;
  style?: React.CSSProperties;
  suffix?: string;
  textBreak?: boolean;
  value: string;
}) {
  const print = usePrint();
  const translations = useTranslations();

  const CustomChip = print ? PrintableChip : Chip;
  const chipStyle =
    style != null
      ? {
          backgroundColor: style.backgroundColor,
          opacity: 0.9,
          primary: true,
          selected: false,
        }
      : {};
  const props = {
    ...computeChipProps(chipProps),
    ...chipStyle,
    textBreak,
  };

  return (
    <div key={getKey(value, keyPrefix)} className="m-1" style={style}>
      <CustomChip {...props}>
        {style != null ? (
          <ChipText
            bold
            dark
            primary
            style={{
              margin: margin(0),
              textDecoration: style.textDecoration,
            }}
            textBreak={textBreak}
          >
            {generatePrefixedString(translations, value, prefix, suffix)}
          </ChipText>
        ) : (
          generatePrefixedString(translations, value, prefix, suffix)
        )}
      </CustomChip>
    </div>
  );
}

export function ArrayChip({
  bold,
  disabled,
  getOntology,
  keyPrefix,
  primary,
  selected,
  type,
  value,
}: {
  bold?: boolean;
  disabled?: boolean;
  getOntology: GetOntologyType;
  keyPrefix: string;
  primary?: boolean;
  selected?: boolean;
  type: OntologyType;
  value: Array<number> | null | undefined;
}) {
  const chipProps = { bold, disabled, primary, selected };

  if (!value || !Array.isArray(value)) return null;
  return (
    <>
      {value.map((key) => (
        <ChipValue
          key={getKey(key.toString(), keyPrefix)}
          chipProps={chipProps}
          keyPrefix={keyPrefix}
          value={getOntology({ key, type })}
        />
      ))}
    </>
  );
}

export function NumberChip({
  bold,
  disabled,
  keyPrefix,
  prefix,
  primary,
  selected,
  transform,
  value,
}: {
  bold?: boolean;
  disabled?: boolean;
  keyPrefix: string;
  prefix?: string;
  primary?: boolean;
  selected?: boolean;
  transform: (value: number) => string;
  value: number | null | undefined;
}) {
  const chipProps = { bold, disabled, primary, selected };

  if (value == null) return null;
  return (
    <ChipValue
      chipProps={chipProps}
      keyPrefix={keyPrefix}
      prefix={prefix}
      value={transform(value)}
    />
  );
}

export function OntologyChip({
  bold,
  disabled,
  getOntology,
  keyPrefix,

  primary,
  selected,
  type,
  value,
}: {
  bold?: boolean;
  disabled?: boolean;
  getOntology: GetOntologyType;
  keyPrefix: string;
  primary?: boolean;
  selected?: boolean;
  type: OntologyType;
  value: boolean | number | null | undefined;
}) {
  const chipProps = { bold, disabled, primary, selected };

  if (!value || typeof value !== "number") return null;
  return (
    <ChipValue
      chipProps={chipProps}
      keyPrefix={keyPrefix}
      value={getOntology({ key: value, type })}
    />
  );
}

export function BooleanChip({
  bold,
  disabled,
  keyPrefix,
  label,
  prefix,
  primary,
  selected,
  value,
}: {
  bold?: boolean;
  disabled?: boolean;
  keyPrefix: string;
  label: string | { oldValue: string; value: string };
  prefix?: string;
  primary?: boolean;
  selected?: boolean;
  value: boolean;
}) {
  const chipProps = { bold, disabled, primary, selected };

  if (!value) return null;
  const valueLabel = typeof label === "string" ? label : label.value;
  return (
    <ChipValue
      chipProps={chipProps}
      keyPrefix={keyPrefix}
      prefix={prefix}
      value={valueLabel}
    />
  );
}

export function StringChip({
  bold,
  disabled,
  keyPrefix,
  prefix,
  primary,
  selected,
  suffix,
  textBreak,
  value,
}: {
  bold?: boolean;
  disabled?: boolean;
  keyPrefix: string;
  prefix?: string;
  primary?: boolean;
  selected?: boolean;
  suffix?: string;
  textBreak?: boolean;
  value: string | null | undefined;
}) {
  const chipProps = { bold, disabled, primary, selected };

  if (!value) return null;
  return (
    <ChipValue
      chipProps={chipProps}
      keyPrefix={keyPrefix}
      prefix={prefix}
      suffix={suffix}
      textBreak={textBreak}
      value={value}
    />
  );
}

export function EmptiableField({
  bold,
  category,
  noEmptyValue,
  prefix,
  testId,
  title,
  value,
}: EmptiableFieldProps) {
  const translations = useTranslations();

  if (isNullValue(value, noEmptyValue)) return null;

  return category != null ? (
    <CategoryContainer className={category} testId={testId}>
      {title && <Subheading bold>{title}</Subheading>}
      <CategoryDescription
        noEmptyValue={noEmptyValue}
        title={title}
        value={value}
      />
    </CategoryContainer>
  ) : (
    <div className="flex max-w-full flex-wrap" data-testid={testId}>
      {title && (
        <span className="-mr-[28px]">
          <Body
            data-testid="title"
            fontWeight={bold ? FONT_WEIGHT_BOLD : FONT_WEIGHT_REGULAR}
            maxWidth={sizing(45)}
          >
            {`${title}${translations.general.colon} `}
          </Body>
        </span>
      )}
      <CategoryDescription
        noEmptyValue={noEmptyValue}
        prefix={prefix}
        title={title}
        value={value}
      />
    </div>
  );
}

export function CategoryDescription({
  noEmptyValue,
  prefix,
  title,
  value,
}: {
  noEmptyValue?: boolean;
  prefix?: string | null | undefined;
  title?: string;
  value: string | null | undefined;
}) {
  if (isNullValue(value, noEmptyValue)) {
    return null;
  }

  if (isNotEmptyString(value)) {
    return <StringField prefix={prefix} testId="description" value={value} />;
  }

  return <InfoText prefix={prefix} title={title} />;
}

export function OntologyValue({
  getOntology,
  label,
  style,
  type,
  value,
  verticalLayout,
}: {
  getOntology: GetOntologyType;
  label?: string;
  style?: React.CSSProperties;
  type: OntologyType;
  value?: Array<number> | number;
  verticalLayout?: boolean;
}) {
  if (!value) return null;

  if (Array.isArray(value)) {
    const values = verticalLayout
      ? value.map((key) => <div key={key}>{getOntology({ key, type })}</div>)
      : value.map((key) => getOntology({ key, type })).join(", ");
    return (
      <div className="flex">
        <Body style={style}>{values}</Body>
      </div>
    );
  }

  const wrappedContent = (
    <div className="flex">
      <Body style={style}>{getOntology({ key: value, type })}</Body>
    </div>
  );

  return label ? (
    <Category testId={type} title={label}>
      {wrappedContent}
    </Category>
  ) : (
    wrappedContent
  );
}

export function OntologyField({
  getOntology,
  label,
  type,
  value,
  verticalLayout = false,
}: {
  getOntology: GetOntologyType;
  label?: string;
  type: OntologyType;
  value: Array<number> | number | null | undefined;
  verticalLayout?: boolean;
}) {
  if (!value) return null;

  return (
    <OntologyValue
      getOntology={getOntology}
      label={label}
      type={type}
      value={value}
      verticalLayout={verticalLayout}
    />
  );
}

export function BooleanField({
  label,
  value,
}: {
  label: string;
  value: boolean | null | undefined;
}) {
  if (!value) {
    return null;
  }
  return <Body>{label}</Body>;
}

export function LocationContent({
  bold = true,
  hideStreetAndHouseNumber,
  location,
  prefixText,
  testId,
}: {
  bold?: boolean;
  hideStreetAndHouseNumber?: boolean;
  location: Location | null | undefined;
  prefixText?: string | null | undefined;
  testId: string;
}) {
  const translations = useTranslations();
  const decryptedHouseNumber = location?.encrypted_house_number?.decrypted;
  const isDecrypted =
    decryptedHouseNumber != null && decryptedHouseNumber != SEALD_REDACTED;
  const isBlurred = location?.encrypted_house_number != null && !isDecrypted;

  const innerValue =
    location &&
    formatLocation({ hideStreetAndHouseNumber, location, translations });

  if (!innerValue) {
    return null;
  }

  return (
    <div className="flex w-full items-baseline px-4">
      <Prefix
        bold={bold}
        text={prefixText || translations.address.address}
        translations={translations}
      />
      <div className="min-w-0 flex-1">
        <StringValue
          style={{
            margin:
              isBlurred && !hideStreetAndHouseNumber
                ? margin(0, 0, 0, 2)
                : undefined,
            maxWidth: "none",
          }}
          testId={testId}
          value={innerValue}
        />
        {!hideStreetAndHouseNumber && (isBlurred || isDecrypted) && (
          <span className="inline-flex items-baseline">
            {isBlurred && (
              <StringValue
                style={{ ...blurStyle, margin: margin(0, 2, 0, 0.5) }}
                testId={testId}
                value="Blur"
              />
            )}
            <span style={{ display: "inline-block", width: dp(16) }}>
              <Tooltip
                title={
                  isBlurred
                    ? translations.auctionRequest.accessEncryptedDataHint
                    : undefined
                }
              >
                <LockIcon
                  size={lockIconStyle.fontSize}
                  style={{ ...lockIconStyle, transform: translate({ y: 2 }) }}
                />
              </Tooltip>
            </span>
          </span>
        )}
      </div>
    </div>
  );
}

export function StringValue({
  multiLine,
  prefix,
  style,
  testId,
  value,
}: {
  multiLine?: boolean | null | undefined;
  prefix?: string | null | undefined;
  style?: ToType | null | undefined;
  testId: string;
  value: string;
}) {
  const translations = useTranslations();
  const content = generatePrefixedString(translations, value, prefix);

  return multiLine ? (
    <div className="flex flex-col">
      <Body data-testid={testId} style={style}>
        {multiLine ? renderMultiLine(content) : content}
      </Body>
    </div>
  ) : (
    <Body
      data-testid={testId}
      display="inline"
      style={{
        ...style,
        overflowWrap: "break-word",
        whiteSpace: "normal",
      }}
    >
      {content}
    </Body>
  );
}

export function StringField({
  contentMargin,
  multiLine,
  prefix,
  testId,
  value,
}: {
  contentMargin?: string;
  multiLine?: boolean;
  prefix?: string | null | undefined;
  testId: string;
  value: string | null | undefined;
}) {
  if (!value) return null;

  return (
    <StringValue
      multiLine={multiLine}
      prefix={prefix}
      style={{ margin: contentMargin }}
      testId={testId}
      value={value}
    />
  );
}

export function EncryptedField({
  bold = false,
  hideEmpty,
  isSealdOnly,
  longContext,
  prefix,
  suffix,
  testId,
  value,
  verticalLayout = false,
  withDateString,
  withEncryptionAccess,
}: EncryptedFieldProps) {
  const translations = useTranslations();
  const locale = useLocale();
  const decryptedValue = getDecryptedValue(value);

  if (withEncryptionAccess && !value) {
    if (hideEmpty) return null;

    return (
      <BodyWrapper>
        <div className="flex max-w-full items-center">
          <Prefix bold={bold} text={prefix} translations={translations} />
          <StringValue
            style={italic}
            testId={testId}
            value={translations.actions.valueEmtpy}
          />
          <LockIcon size={lockIconStyle.fontSize} style={lockIconStyle} />
        </div>
      </BodyWrapper>
    );
  }

  const withEncryptedValue = isSealdOnly
    ? value?.seald_content
    : value?.content;
  if (withEncryptedValue) {
    if (value && !decryptedValue) {
      return verticalLayout ? (
        <BodyWrapper margin={margin(0, 0, 0, 2)}>
          <div
            className="!min-width-0 flex max-w-full flex-col"
            style={{ minWidth: 0 }}
          >
            <div
              className="flex"
              style={{
                alignItems: "baseline",
                flexWrap: "wrap",
                minWidth: 0,
                width: "100%",
              }}
            >
              <Prefix
                bold={bold}
                endContent={
                  <LockSecurePin
                    style={{ ...lockIconStyle, margin: margin(0.4, 0, 0, 0.5) }}
                    tooltip={
                      translations.auctionRequest.accessEncryptedDataHint
                    }
                  />
                }
                text={prefix}
                translations={translations}
              />
            </div>
            <StringValue
              style={{ ...blurStyle, margin: margin(0, 0.75) }}
              testId={testId}
              value="Blurred Data"
            />
          </div>
        </BodyWrapper>
      ) : (
        <BodyWrapper>
          <div className="flex max-w-full items-center">
            <Prefix bold={bold} text={prefix} translations={translations} />
            <StringValue
              style={blurStyle}
              testId={testId}
              value="Blurred Data"
            />
            <Tooltip
              title={translations.auctionRequest.accessEncryptedDataHint}
            >
              <LockIcon size={lockIconStyle.fontSize} style={lockIconStyle} />
            </Tooltip>
          </div>
        </BodyWrapper>
      );
    }
  }
  if (decryptedValue) {
    const prefixAndValue = generatePrefixedString(
      translations,
      decryptedValue,
      prefix,
      suffix,
    );
    if (longContext) {
      return (
        <div className="flex">
          <Body>
            <PrettyStringAndLockIcon value={prefixAndValue} />
          </Body>
        </div>
      );
    }
    const formattedValue = withDateString
      ? convertBirthDateIn(value, { locale }) || ""
      : decryptedValue;

    if (verticalLayout) {
      const colonedPrefix = getColonedPrefix(prefix, translations);

      return (
        <div className="flex">
          <Body>
            <span className="font-bold">
              <PrettyStringAndLockIcon value={colonedPrefix} />
            </span>
            <br />
            <StringValue
              style={{ margin: margin(0) }}
              testId={testId}
              value={
                suffix ? formattedValue.concat(` ${suffix}`) : formattedValue
              }
            />
            <br className="mb-0.5" />
          </Body>
        </div>
      );
    }
    return (
      <BodyWrapper>
        <div className="flex max-w-full items-center">
          <Prefix bold={bold} text={prefix} translations={translations} />
          <StringValue
            testId={testId}
            value={
              suffix ? formattedValue.concat(` ${suffix}`) : formattedValue
            }
          />
          <LockIcon size={lockIconStyle.fontSize} style={lockIconStyle} />
        </div>
      </BodyWrapper>
    );
  }
  return null;
}

export function EmptiableCategory({
  children,
  empty,
  hasEmptyField,
  testId,
  title,
}: {
  children?: React.ReactNode;
  empty?: boolean;
  hasEmptyField?: boolean;
  testId: string;
  title: string;
}) {
  return (
    <Category testId={testId} title={title}>
      {empty ? <InfoText title={title} /> : children}
      {hasEmptyField && <InfoText />}
    </Category>
  );
}

function CareseekerInfoText({
  prefix,
  title,
}: {
  prefix?: string | null;
  title: string;
}) {
  const translations = useTranslations();
  const { trackEvent } = useTracking();
  const { patient: patientNavigation } = useCareseekerNavigationHandlers();
  const { assessmentSlug, auctionId, patientId } = usePatientInfoSlug();

  return (
    <Body
      data-testid={`add-missing-${title.replace(/ /g, "-").toLowerCase()}`}
      fontWeight="bold"
      primary
      style={{ cursor: "pointer" }}
      onClick={() => {
        trackEvent({
          name: TRACK_EVENTS.MISSING_FIELD,
          page: assessmentSlug,
        });
        patientNavigation.goToassessmentSlug({
          assessmentSlug,
          auctionId,
          patientId,
        });
      }}
    >
      {prefix && (
        <Body data-testid="description" display="inline" margin={margin(0)}>
          {`${getColonedPrefix(prefix, translations)} `}
        </Body>
      )}
      {translations.auctionResponse.areYouSureNotAddInfo}
    </Body>
  );
}

function InfoText({
  prefix,
  title = "",
}: {
  prefix?: string | null | undefined;
  title?: string;
}) {
  const { isReceiverApp, isSenderApp } = useEnvContext();
  const translations = useTranslations();
  const { auction } = usePatientInfoContext();

  const editSearchAction = useMemo(
    () => auction && findSearchAction(auction, SEARCH_ACTION_EDIT_SEARCH),
    [auction?.search_actions],
  );

  if (isReceiverApp) {
    return (
      <Body>
        <div style={{ display: "flex" }}>
          {translations.auctionRequest.noAdditionalInformationProvided}
        </div>
      </Body>
    );
  }

  if (isSenderApp && editSearchAction) {
    return <CareseekerInfoText prefix={prefix} title={title} />;
  }

  return null;
}

export function Category({
  children,
  testId,
  title,
}: {
  children?: React.ReactNode;
  testId: string;
  title: React.ReactNode | string | null | undefined;
}) {
  return (
    <CategoryContainer testId={testId}>
      <Subheading bold>{title}</Subheading>
      {children}
    </CategoryContainer>
  );
}

export function Categories({
  auction,
  categories,
  forClinic,
  getOntology,
  translations,
}: {
  auction: Auction;
  categories: CategoryType[];
  forClinic?: boolean;
  getOntology: GetOntologyType;
  translations: Translations;
}) {
  const { isDesktop } = useTailwindMedia();
  const { isSealdOnly, withEncryptionAccess } = useEncryption();

  const validCategories = categories.filter(
    (category) => category.exists && category.Component !== null,
  );

  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: dp(0) }}>
      {validCategories.map(
        ({ Component, fullWidth, key, notEmpty, valueGetter }) => {
          const width =
            !isDesktop || fullWidth ? "100%" : `calc(50% - ${dp(0)})`;
          return (
            <div key={key} data-testid={key} style={{ width }}>
              <Component
                auction={auction}
                empty={!notEmpty}
                forClinic={forClinic}
                getOntology={getOntology}
                isSealdOnly={isSealdOnly}
                translations={translations}
                value={valueGetter(auction)}
                withEncryptionAccess={withEncryptionAccess}
              />
            </div>
          );
        },
      )}
    </div>
  );
}

function PrintCard({
  children,
  title,
}: {
  children: React.ReactNode;
  title?: string;
}) {
  return (
    <div style={{ padding: padding(2, 3) }}>
      {title && <Title>{title}</Title>}
      <div className="flex flex-col">{children}</div>
    </div>
  );
}

export function PatientProfileInfoCard({
  children,
  noDivider,
  startContent,
  testId,
  title,
}: {
  children: React.ReactNode;
  noDivider?: boolean;
  title?: string;
  startContent?: React.ReactNode;
  testId?: string;
}) {
  const { isReceiverApp, isSenderApp } = useEnvContext();
  const print = usePrint();

  if (print && isReceiverApp) {
    return <PrintCard title={title}>{children}</PrintCard>;
  }

  const Card = isReceiverApp ? InfoCard : InfoCardLite;

  return (
    <Card
      cardMargin={margin(0, 0, isReceiverApp ? 2 : 0, 0)}
      noDivider={isSenderApp ? noDivider : undefined}
      startContent={startContent}
      testId={testId}
      title={title}
      titleMargin={isReceiverApp ? margin(0) : margin(2, 2, 1, 2)}
    >
      <div
        className={clsx("flex w-full flex-col bg-white", {
          "border border-t-0 border-gray-200 p-3": isReceiverApp,
        })}
      >
        {children}
      </div>
    </Card>
  );
}

export const getAdmissionsMainContactValue = (auction: Auction) => {
  const patientIsMainContact =
    auction.admissions?.profile?.is_patient_main_contact;

  const firstName = patientIsMainContact
    ? auction.patient.profile?.first_name
    : auction.admissions?.profile?.main_contact_first_name;
  const lastName = patientIsMainContact
    ? auction.patient.profile?.last_name
    : auction.admissions?.profile?.main_contact_last_name;

  return {
    firstName: firstName,
    fullName: [firstName?.decrypted, lastName?.decrypted].truthy().join(" "),
    lastName: lastName,
  } as const;
};
